import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { formatCivilDate } from '@eis/core';
import { DetailsSection, StatTiles } from '@eis/patterns';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { PartnerList } from '../../policies/ui/PartnerList';
import { useGroupPolicyPremiumSummary } from '../../premium/api/premium';
import { formatAmount } from '../../premium/model/format';

/**
 * Übersicht tab.
 *
 * Configuration detail — the pinned tariff binding and the digest — sits behind ViewLevel
 * `advanced`. That is the I-6 resolution applied to a screen: the operations persona gets the
 * Gruppen-Police without configuration jargon, and the information is still one toggle away
 * for whoever needs it. Even there it is labelled as a BINDING, never as a version the reader is
 * invited to change: in-force agreements do not migrate when a new group tarif version is
 * published (SSOT §5.3).
 */
export function GroupPolicyOverviewTab({
  groupPolicy,
  snapshot,
  partnerRoles,
  locale,
}: {
  readonly groupPolicy: InstanceApi.GroupPolicy;
  readonly snapshot: InstanceApi.SnapshotEnvelope | undefined;
  /** The group version's roles, through the pin — for the role names (SDD-011 P-8). */
  readonly partnerRoles: readonly InstanceApi.PartnerRoleRule[] | undefined;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const billing = groupPolicy.billing;
  const payload = groupPolicy.data ?? {};

  const civil = (value: string | undefined): string =>
    value === undefined || value === '' ? '—' : formatCivilDate(value, locale);
  // *Prämienvolumen* (SDD-021 §9, INSU-632/633): the monthly gross at TODAY, from the server-side
  // summary over all members — the same read the members tab shows, at the service's today.
  const summary = useGroupPolicyPremiumSummary(groupPolicy.code ?? '');
  const volume = summary.data?.summary;
  const pending = <Skeleton variant="text" width={96} />;

  return (
    <Stack spacing={2.5}>
      <StatTiles
        label={t('premium.volume.kpiLabel')}
        items={[
          {
            label: t('premium.volume.label'),
            value: summary.isPending
              ? pending
              : formatAmount(volume?.total?.gross, volume?.currency, locale),
            tone: 'accent',
          },
          {
            label: t('premium.volume.members'),
            value: summary.isPending ? pending : String(volume?.memberCount ?? '—'),
          },
        ]}
      />
      {/* Partner and Abrechnung side by side (design v2, prompt 24 Phase 24.4); the rest below. */}
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DetailsSection
            title={t('groupPolicies.partners.title')}
            description={t('groupPolicies.partners.description')}
            fields={[
              {
                // Every party per role, resolved (SDD-011): the name when the directory knows one,
                // the id marked unresolved when not (SSOT §4.2). `policyholderPartnerId` is its
                // projection (P-9).
                label: t('groupPolicies.partners.title'),
                wide: true,
                value: <PartnerList partners={groupPolicy.partners} rules={partnerRoles} />,
              },
              {
                label: t('groupPolicies.overview.partnerId'),
                value: groupPolicy.policyholderPartnerId ?? '—',
              },
              { label: t('groupPolicies.overview.validFrom'), value: civil(snapshot?.validFrom) },
              { label: t('groupPolicies.overview.validTo'), value: civil(snapshot?.validTo) },
            ]}
            advancedFields={[
              { label: t('groupPolicies.overview.recordedAt'), value: snapshot?.recordedAt ?? '—' },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DetailsSection
            title={t('groupPolicies.overview.billing')}
            description={t('groupPolicies.overview.billingDescription')}
            fields={[
              {
                label: t('groupPolicies.overview.frequency'),
                value:
                  billing?.frequency === undefined
                    ? '—'
                    : t(`groupPolicies.frequency.${billing.frequency}` as never),
              },
              {
                // Dunning is master-only by domain rule, so this row can only ever read one way
                // (SSOT I-20).
                label: t('groupPolicies.overview.dunning'),
                value: t('groupPolicies.overview.dunningValue'),
              },
            ]}
            advancedFields={[
              {
                label: t('groupPolicies.overview.mode'),
                value:
                  billing?.mode === undefined
                    ? '—'
                    : t(`groupPolicies.mode.${billing.mode}` as never),
              },
              {
                label: t('groupPolicies.overview.tariffBinding'),
                value: groupPolicy.configuration?.groupTarifVersionErn ?? '—',
                source: (
                  <Alert severity="info" icon={false} sx={{ py: 0 }}>
                    {t('groupPolicies.overview.tariffBindingHint')}
                  </Alert>
                ),
                wide: true,
              },
            ]}
          />
        </Grid>
      </Grid>

      {/*
        Verknüpfte Produkte is the INSTANCE-level allowed-tarifs list (SSOT I-1) and it is the gate
        for creating members — which is why it is stated as a rule, not as a tag cloud.
      */}
      <DetailsSection
        title={t('groupPolicies.overview.linkedProducts')}
        description={t('groupPolicies.overview.linkedProductsDescription')}
        fields={[
          {
            label: t('groupPolicies.overview.linkedProducts'),
            wide: true,
            value:
              (groupPolicy.allowedTarifs ?? []).length === 0 ? (
                <Alert severity="warning">{t('groupPolicies.overview.noLinkedProducts')}</Alert>
              ) : (
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {(groupPolicy.allowedTarifs ?? []).map((tarifErn) => (
                    <Chip key={tarifErn} label={tarifErn} variant="outlined" />
                  ))}
                </Stack>
              ),
          },
        ]}
      />

      {Object.keys(payload).length > 0 && (
        <DetailsSection
          title={t('groupPolicies.overview.terms')}
          description={t('groupPolicies.overview.termsDescription')}
          // The payload is schema-validated JSON whose shape this screen does not know, so it
          // is rendered as JSON rather than coerced: `String({})` is `[object Object]`, which
          // tells a reader nothing and looks like a bug. A string is shown as itself — a contract
          // or external reference (SDD-022 D-6: `UNI-884210`) read in quotes looks like code.
          fields={Object.entries(payload).map(([key, value]) => ({
            label: key,
            value: typeof value === 'string' ? value : JSON.stringify(value),
          }))}
        />
      )}
    </Stack>
  );
}
